import React, { useEffect, useMemo, useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';
import { isFunction } from 'lodash/lang';

import { StudyApi } from '../../../../../../api';
import Icon from '../../../../../../common/general/Icon';
import NotificationManager from '../../../../../../common/notifications/NotificationManager';
import {
  PRIMARY_CONTACT_FILL_NOT_CORRECTLY,
  PRIMARY_CONTACT_UPDATED
} from '../../../../../../constants/notificationMessages';
import { countries, getCountryById } from '../../../../../../services/geo';
import {
  formatDialCode,
  normalizePhoneNumber,
  serializePhoneNumber,
  validatePhoneNumber
} from '../../../../../../services/geo/e-164';
import { onRequestError } from '../../../../../../services/handlers';
import { caseInsensitiveStringIncludes } from '../../../../../../services/string';

import './EditPatientPrimaryContact.scss';

export default function EditPatientPrimaryContact({
  newPageHeader,
  primaryPhoneCode,
  contactNumber,
  neverCallAgain,
  voicemailNotAllowed,
  patientUniqueIdentifier,
  onSuccessfulUpdate
}) {
  const anchorRef = useRef(null);
  const [value, setValue] = useState({ countryId: null, nationalNumber: '' });
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { countryId, nationalNumber } = value;

  const matchedCountry = useMemo(() => {
    return getCountryById(countryId);
  }, [countryId]);

  const filteredOptions = countries.filter(function(country) {
    return caseInsensitiveStringIncludes(`${country.id} ${country.name} ${formatDialCode(country.dialCode)}`, search);
  });

  useEffect(() => {
    if (!primaryPhoneCode || !contactNumber) {
      return;
    }
    setValue(function(state) {
      return {
        ...state,
        countryId: primaryPhoneCode,
        nationalNumber: normalizePhoneNumber(contactNumber, primaryPhoneCode)
      };
    });
  }, [contactNumber, primaryPhoneCode]);

  return (
    <div className="edit-patient-primary-contact">
      {!newPageHeader && (
        <>
          |<label className="pt-sub px-2 m-0 ">Primary Contact</label>
        </>
      )}
      <div className="edit-patient-primary-contact-field-group">
        <ClickAwayListener onClickAway={handleClose}>
          <Box sx={{ position: 'relative', width: 75 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleOpen}
              endIcon={<ExpandMoreIcon />}
              ref={anchorRef}
              sx={{ justifyContent: 'space-between', textTransform: 'none', height: '40px' }}
            >
              {renderValue(matchedCountry)}
            </Button>

            <Menu
              open={open}
              anchorEl={anchorRef.current}
              onClose={handleClose}
              slotProps={{
                paper: {
                  sx: { width: 200, maxHeight: 400 }
                }
              }}
            >
              <Box
                sx={{
                  px: 1,
                  py: 1
                }}
              >
                <TextField
                  hiddenLabel
                  placeholder="Search..."
                  variant="outlined"
                  size="small"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  fullWidth
                />
              </Box>

              <List dense disablePadding>
                {filteredOptions.length === 0 && (
                  <ListItem>
                    <ListItemText primary="No options found." />
                  </ListItem>
                )}
                {filteredOptions.map(option => (
                  <MenuItem
                    style={{ whiteSpace: 'normal' }}
                    key={option.id}
                    selected={option.id === countryId}
                    onClick={() => handleSelect(option)}
                  >
                    {renderOption ? renderOption(option) : option.name}
                  </MenuItem>
                ))}
              </List>
            </Menu>
          </Box>
        </ClickAwayListener>
        <TextField
          hiddenLabel
          variant="outlined"
          size="small"
          sx={{ width: '160px', textDecoration: neverCallAgain ? 'line-through' : 'none' }}
          value={nationalNumber}
          onChange={handleInputChange}
          disabled={neverCallAgain}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start" sx={{ marginTop: '2px' }}>
                  {formatDialCode(matchedCountry?.dialCode)}
                </InputAdornment>
              )
            }
          }}
        />
        {!neverCallAgain && (
          <IconButton color="primary" onClick={handleSave} disabled={saveButtonIsDisabled()}>
            <SaveIcon />
          </IconButton>
        )}
        {!neverCallAgain && voicemailNotAllowed && (
          <Icon className="voicemail-not-allowed-icon eds-icon-btn" title="Voicemail Not Allowed">
            voicemail
          </Icon>
        )}
        {neverCallAgain && <span className="text-danger pl-2">Do Not Call</span>}
      </div>
    </div>
  );
  function saveButtonIsDisabled() {
    return (
      serializePhoneNumber(contactNumber) === serializePhoneNumber(nationalNumber) && primaryPhoneCode === countryId
    );
  }

  function handleSave() {
    const error = validatePhoneNumber(countryId, nationalNumber);

    if (error) {
      NotificationManager.error(`${PRIMARY_CONTACT_FILL_NOT_CORRECTLY}. ${error}`);
      return;
    }

    const contactNumber = serializePhoneNumber(nationalNumber);

    StudyApi.updateContactNumber({
      phoneCode: countryId,
      contactNumber,
      patientUniqueIdentifier
    }).then(function() {
      NotificationManager.success(PRIMARY_CONTACT_UPDATED);
      if (isFunction(onSuccessfulUpdate)) {
        onSuccessfulUpdate({
          primaryPhoneCode: countryId,
          contactNumber
        });
      }
    }, onRequestError);
  }

  function handleInputChange({ target }) {
    if (!countryId) {
      return;
    }
    setValue(function(state) {
      return {
        ...state,
        countryId,
        nationalNumber: normalizePhoneNumber(target.value, countryId)
      };
    });
  }

  function handleSelect(country) {
    setValue(function(state) {
      return {
        ...state,
        countryId: country?.id || null,
        nationalNumber: normalizePhoneNumber(nationalNumber, country.id)
      };
    });
    handleClose();
  }

  function handleClose() {
    setSearch('');
    setOpen(false);
  }

  function handleOpen() {
    return setOpen(true);
  }
}

function renderOption(country) {
  if (!country.id) {
    return null;
  }
  return `${country.name} (${country.id}) ${formatDialCode(country.dialCode)}`;
}

function renderValue(country) {
  return country ? country.id : <Typography color="textSecondary">N/A</Typography>;
}
